import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';

type CommitmentType = 'sport' | 'music' | 'work' | 'other';

const DAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

const TYPE_ICONS: Record<CommitmentType, string> = {
  sport: '⚽', music: '🎵', work: '💼', other: '📌',
};

const TYPE_COLORS: Record<CommitmentType, { bg: string; text: string }> = {
  sport: { bg: '#DCFCE7', text: '#15803D' },
  music: { bg: '#EDE9FE', text: '#6D28D9' },
  work:  { bg: '#DBEAFE', text: '#1D4ED8' },
  other: { bg: '#F3F4F6', text: '#4B5563' },
};

interface Commitment {
  commitment_id: string;
  label: string;
  type: CommitmentType;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

interface CommitmentsResponse {
  commitments: Commitment[];
}

interface CommitmentForm {
  label: string;
  type: CommitmentType;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

const emptyForm = (): CommitmentForm => ({
  label: '',
  type: 'sport',
  day_of_week: 'monday',
  start_time: '17:00',
  end_time: '18:00',
});

const COMMITMENT_TYPE_VALUES: CommitmentType[] = ['sport', 'music', 'work', 'other'];

export function ManageCommitmentsPage() {
  const { t } = useTranslation();
  const { childId } = useAuthStore();

  const { data, isLoading } = useData<CommitmentsResponse>(
    childId ? `/children/${childId}/commitments` : null,
  );

  const [localItems, setLocalItems] = useState<Commitment[]>([]);
  const [form, setForm] = useState<CommitmentForm>(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    if (data?.commitments) {
      setLocalItems(data.commitments);
    }
  }, [data]);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = async () => {
    if (!childId || !form.label.trim()) return;
    setIsSaving(true);
    try {
      const res = await apiFetch<{ commitment_id: string }>(
        `/children/${childId}/commitments`,
        {
          method: 'POST',
          body: JSON.stringify({
            label: form.label.trim(),
            type: form.type,
            day_of_week: form.day_of_week,
            start_time: form.start_time,
            end_time: form.end_time,
          }),
        },
      );
      setLocalItems((prev) => [
        ...prev,
        {
          commitment_id: res.commitment_id,
          label: form.label.trim(),
          type: form.type,
          day_of_week: form.day_of_week,
          start_time: form.start_time,
          end_time: form.end_time,
        },
      ]);
      setForm(emptyForm());
      showToast(t('manage_commitments.added_toast'));
    } catch {
      showToast(t('manage_commitments.add_failed_toast'), false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!childId) return;
    setDeletingId(id);
    try {
      await apiFetch(`/children/${childId}/commitments/${id}`, { method: 'DELETE' });
      setLocalItems((prev) => prev.filter((c) => c.commitment_id !== id));
      showToast(t('manage_commitments.removed_toast'));
    } catch {
      showToast(t('manage_commitments.delete_failed_toast'), false);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/student/timetable" />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          <p style={{ margin: '0 0 2px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--primary)' }}>
            VADOStudy
          </p>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 20px' }}>
            {t('manage_commitments.title')}
          </h1>

          {/* ── Add Activity form ── */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 16px' }}>
              {t('manage_commitments.add_activity')}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                  {t('manage_commitments.name_label')}
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder={t('manage_commitments.name_placeholder')}
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                  {t('manage_commitments.type_label')}
                </label>
                <select
                  className="input"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as CommitmentType }))}
                >
                  {COMMITMENT_TYPE_VALUES.map((value) => (
                    <option key={value} value={value}>
                      {TYPE_ICONS[value]} {t(`student_timetable.commitment_types.${value}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                  {t('manage_commitments.day_label')}
                </label>
                <select
                  className="input"
                  value={form.day_of_week}
                  onChange={(e) => setForm((f) => ({ ...f, day_of_week: e.target.value }))}
                >
                  {DAYS.map((d) => (
                    <option key={d} value={d}>{t(`student_timetable.days_full.${d}`)}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                    {t('manage_commitments.start_time_label')}
                  </label>
                  <input
                    type="time"
                    className="input"
                    value={form.start_time}
                    onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                    {t('manage_commitments.end_time_label')}
                  </label>
                  <input
                    type="time"
                    className="input"
                    value={form.end_time}
                    onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAdd}
                disabled={isSaving || !form.label.trim()}
                style={{ width: '100%' }}
              >
                {isSaving ? t('actions.saving') : t('manage_commitments.save_activity')}
              </button>
            </div>
          </div>

          {/* ── Current activities list ── */}
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
            {t('manage_commitments.current_activities')}
          </h2>

          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div className="loading-skeleton" style={{ height: '72px', borderRadius: '12px' }} />
              <div className="loading-skeleton" style={{ height: '72px', borderRadius: '12px' }} />
            </div>
          )}

          {!isLoading && localItems.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '24px 0' }}>
              {t('manage_commitments.empty')}
            </p>
          )}

          {!isLoading && localItems.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {localItems.map((item) => {
                const colors = TYPE_COLORS[item.type] ?? TYPE_COLORS.other;
                const isDeleting = deletingId === item.commitment_id;
                return (
                  <div
                    key={item.commitment_id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'white', borderRadius: '12px', padding: '14px 16px',
                      border: '1px solid var(--border)',
                      opacity: isDeleting ? 0.5 : 1,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.label}
                        </p>
                        <span style={{
                          background: colors.bg, color: colors.text,
                          fontSize: '0.7rem', fontWeight: 600,
                          padding: '2px 8px', borderRadius: '999px',
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}>
                          {t(`student_timetable.commitment_types.${item.type}`)}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {t(`student_timetable.days_full.${item.day_of_week}`, { defaultValue: item.day_of_week })}
                        {' · '}
                        {item.start_time}–{item.end_time}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleDelete(item.commitment_id)}
                      disabled={isDeleting}
                      aria-label={t('manage_commitments.delete_aria', { label: item.label })}
                      style={{
                        background: 'none', border: '1px solid var(--danger)',
                        borderRadius: '8px', cursor: 'pointer',
                        color: 'var(--danger)', fontSize: '0.8rem', fontWeight: 600,
                        padding: '4px 10px', marginLeft: '12px', flexShrink: 0,
                        opacity: isDeleting ? 0.5 : 1,
                      }}
                    >
                      {isDeleting ? '…' : t('actions.delete')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* ── Toast ── */}
        {toast && (
          <div style={{
            position: 'absolute', bottom: '24px', left: '16px', right: '16px',
            background: toast.ok ? 'var(--text-dark)' : 'var(--danger)',
            color: 'white', borderRadius: '10px', padding: '12px 16px',
            fontSize: '0.9rem', fontWeight: 500, textAlign: 'center', zIndex: 100,
          }}>
            {toast.msg}
          </div>
        )}

      </div>
    </div>
  );
}
